import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    archiveStandardSchedule,
    createProjectCategory,
    createStandardSchedule,
    deactivateProjectCategory,
    getStandardSchedule,
    listProjectCategories,
    listStandardSchedules,
    listStandardVersions,
    publishStandardSchedule,
    updateProjectCategory,
    updateStandardSchedule,
} from '../../../../services/epcStandardScheduleService';
import type { CapacityBasis, StandardStatus, StandardTask } from '../types';

const CATEGORIES_ROOT = ['epc', 'project-categories'] as const;
const STANDARDS_ROOT = ['epc', 'standard-schedules'] as const;

// ---------------------------------------------------------------------------
// Project categories
// ---------------------------------------------------------------------------

export function useProjectCategories(projectTypeId?: string, includeInactive?: boolean) {
    return useQuery({
        queryKey: [...CATEGORIES_ROOT, 'list', { projectTypeId, includeInactive }],
        queryFn: () => listProjectCategories({ projectTypeId, includeInactive }),
    });
}

function useInvalidateCategories() {
    const qc = useQueryClient();
    return () => qc.invalidateQueries({ queryKey: [...CATEGORIES_ROOT] });
}

export function useCreateCategory() {
    const invalidate = useInvalidateCategories();
    return useMutation({
        mutationFn: (input: {
            projectTypeId: string;
            key: string;
            label: string;
            description?: string;
            sequence?: number;
        }) => createProjectCategory(input),
        onSuccess: invalidate,
    });
}

export function useUpdateCategory() {
    const invalidate = useInvalidateCategories();
    return useMutation({
        mutationFn: ({
            categoryId,
            input,
        }: {
            categoryId: string;
            input: { label?: string; description?: string; sequence?: number; active?: boolean };
        }) => updateProjectCategory(categoryId, input),
        onSuccess: invalidate,
    });
}

export function useDeactivateCategory() {
    const invalidate = useInvalidateCategories();
    return useMutation({
        mutationFn: (categoryId: string) => deactivateProjectCategory(categoryId),
        onSuccess: invalidate,
    });
}

// ---------------------------------------------------------------------------
// Standard schedules
// ---------------------------------------------------------------------------

export function useStandardSchedules(filters: {
    projectTypeId?: string;
    projectCategoryId?: string;
    status?: StandardStatus;
    includeArchived?: boolean;
}) {
    return useQuery({
        queryKey: [...STANDARDS_ROOT, 'list', filters],
        queryFn: () => listStandardSchedules(filters),
    });
}

export function useStandardSchedule(standardId: string | null) {
    return useQuery({
        queryKey: [...STANDARDS_ROOT, 'detail', standardId],
        queryFn: () => getStandardSchedule(standardId as string),
        enabled: !!standardId,
    });
}

export function useStandardVersions(standardId: string | null) {
    return useQuery({
        queryKey: [...STANDARDS_ROOT, 'versions', standardId],
        queryFn: () => listStandardVersions(standardId as string),
        enabled: !!standardId,
    });
}

function useInvalidateStandards() {
    const qc = useQueryClient();
    return () => qc.invalidateQueries({ queryKey: [...STANDARDS_ROOT] });
}

export function useCreateStandard() {
    const invalidate = useInvalidateStandards();
    return useMutation({
        mutationFn: (input: {
            name: string;
            description?: string;
            projectTypeId: string;
            projectCategoryId: string;
            capacityBasisDefault?: CapacityBasis;
        }) => createStandardSchedule(input),
        onSuccess: invalidate,
    });
}

export function useUpdateStandard() {
    const invalidate = useInvalidateStandards();
    return useMutation({
        mutationFn: ({
            standardId,
            input,
        }: {
            standardId: string;
            input: {
                name?: string;
                description?: string;
                capacityBasisDefault?: CapacityBasis;
                draftTasks?: StandardTask[];
            };
        }) => updateStandardSchedule(standardId, input),
        onSuccess: invalidate,
    });
}

export function useArchiveStandard() {
    const invalidate = useInvalidateStandards();
    return useMutation({
        mutationFn: ({
            standardId,
            force,
        }: {
            standardId: string;
            force?: boolean;
        }) => archiveStandardSchedule(standardId, force ?? false),
        onSuccess: invalidate,
    });
}

export function usePublishStandard() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            standardId,
            changeNote,
        }: {
            standardId: string;
            changeNote?: string;
        }) => publishStandardSchedule(standardId, changeNote),
        onSuccess: (_result, { standardId }) => {
            qc.invalidateQueries({ queryKey: [...STANDARDS_ROOT] });
            // Explicit per plan: publish flips status/latestVersion on the open detail view.
            qc.invalidateQueries({ queryKey: [...STANDARDS_ROOT, 'detail', standardId] });
        },
    });
}
